// src/components/RealtimeMonitor.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Box, Typography, Paper, Alert, CircularProgress, Chip, Link } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'; // 清醒
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied'; // 严重疲劳
import SnoozeIcon from '@mui/icons-material/Snooze'; // 闭眼
import AirlineSeatIndividualSuiteIcon from '@mui/icons-material/AirlineSeatIndividualSuite'; // 轻度疲劳
import InsertLinkIcon from '@mui/icons-material/InsertLink'; // 报告链接
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // 通用错误
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // 警告
import axios from 'axios';

// --- 配置 & 状态映射 ---
const API_BASE_URL = 'http://localhost:5001'; 
const fatigueStatusMap = { //状态映射
    '清醒': { text: '清醒', icon: <SentimentSatisfiedAltIcon />, color: 'success' },
    '轻度疲劳': { text: '轻度疲劳', icon: <AirlineSeatIndividualSuiteIcon />, color: 'warning' },
    '严重疲劳': { text: '严重疲劳', icon: <SentimentVeryDissatisfiedIcon />, color: 'error' },
    '轻度疲劳 (哈欠)': { text: '轻度疲劳 (哈欠)', icon: <AirlineSeatIndividualSuiteIcon />, color: 'warning' },
    '严重疲劳 (闭眼)': { text: '严重疲劳 (闭眼)', icon: <SnoozeIcon />, color: 'error' },
    '严重疲劳 (闭眼+哈欠)': { text: '严重疲劳 (闭眼+哈欠)', icon: <SentimentVeryDissatisfiedIcon />, color: 'error' },
    '分析错误': { text: '分析错误', icon: <ErrorOutlineIcon />, color: 'default' },
    '未知': { text: '状态未知', icon: <SentimentSatisfiedAltIcon />, color: 'default' },
};
const defaultStatus = { text: '等待分析...', icon: <CircularProgress size={18} />, color: 'info' };
const FRAME_ANALYSIS_DELAY = 150; // ms

function RealtimeMonitor() {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [reportUrl, setReportUrl] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const isProcessingRef = useRef(false);
  const nextFrameTimeoutRef = useRef(null);
  const isStoppingRef = useRef(false);

  // --- Callbacks ---

  const captureFrame = useCallback(() => {
    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;
    if (!videoElement || !canvasElement || videoElement.readyState < videoElement.HAVE_METADATA || videoElement.paused || videoElement.ended) {
        console.warn("捕获帧失败：视频未准备好或已停止。"); return null;
    }
    const videoWidth = videoElement.videoWidth; const videoHeight = videoElement.videoHeight;
    if (!videoWidth || !videoHeight) {
        console.warn("捕获帧失败：无法获取视频尺寸。"); return null;
    }
    canvasElement.width = videoWidth; canvasElement.height = videoHeight;
    const context = canvasElement.getContext('2d');
    context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);
    try { return canvasElement.toDataURL('image/jpeg', 0.8); }
    catch (e) { console.error("无法将 Canvas 转换为 DataURL:", e); return null; }
  }, []);

  const stopMonitoringInternal = useCallback(async (informBackend = true, explicitSessionId = null) => {
     if (isStoppingRef.current) { console.log(`stopMonitoringInternal: 已在停止过程中，忽略。`); return; }
     const sessionIdToStop = explicitSessionId ?? sessionId;
     console.log(`stopMonitoringInternal: 请求停止。通知后端: ${informBackend}, SessionID to Stop: ${sessionIdToStop?.substring(0,8)}..., 当前 state.isMonitoring: ${isMonitoring}`);
     isStoppingRef.current = true; setIsLoading(true);
     clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = null; isProcessingRef.current = false;
     setIsMonitoring(false); // 先设置状态
     try { /* 清理 stream 和 videoRef */
       if (stream) { stream.getTracks().forEach(track => track.stop()); console.log(`stopMonitoringInternal: 本地摄像头媒体流已停止。`); }
       if (videoRef.current && videoRef.current.srcObject) { videoRef.current.srcObject = null; videoRef.current.pause(); videoRef.current.load(); console.log(`stopMonitoringInternal: Video 元素资源已清理。`); }
     } catch (cleanupError) { console.error("清理本地媒体流或 video 元素时出错:", cleanupError); }
     finally { setStream(null); }
     setAnalysisResult(null);

     if (informBackend && sessionIdToStop) {
        console.log(`stopMonitoringInternal: 正在通知后端停止会话 ${sessionIdToStop}...`);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/realtime/stop`, { sessionId: sessionIdToStop });
            console.log(`stopMonitoringInternal: 后端停止会话 ${sessionIdToStop} 成功响应:`, response.data);
            if (response.data?.reportUrl) { setReportUrl(response.data.reportUrl); setError(''); }
            else if (response.data?.error) { setError(`会话已停止，但未能获取报告: ${response.data.error}`); }
        } catch (err) { /* 处理 404 等错误 */
            console.error(`stopMonitoringInternal: 通知后端停止会话 ${sessionIdToStop} 失败:`, err);
            if (err.response?.status === 404) { setError(`停止失败: 后端未找到会话 ${sessionIdToStop?.substring(0,8)}... (可能已过期或无效)`); }
            else { setError(`停止后端会话时出错: ${err.response?.data?.error || err.message}`); }
        } finally { setSessionId(null); }
     } else {
        console.log(`stopMonitoringInternal: 未通知后端 (informBackend=${informBackend}, sessionIdToStop=${sessionIdToStop})。`);
        setSessionId(null);
     }
     setIsLoading(false); isStoppingRef.current = false;
     console.log(`stopMonitoringInternal: 停止流程执行完毕。`);
  }, [sessionId, stream, isMonitoring]);

  // sendFrameToAPI
  const sendFrameToAPI = useCallback(async (imageDataUrl) => {
    const currentSessionId = sessionId;
    if (!imageDataUrl || !currentSessionId) { /* 处理错误, 调用 stopMonitoringInternal(false) */
       console.error("sendFrameToAPI: 无法发送帧，图像数据或 Session ID 缺失。"); setError("内部错误：无法发送数据，请停止重试。");
       if (isMonitoring) { stopMonitoringInternal(false); } return;
    }
    isProcessingRef.current = true;
    try {
      const response = await axios.post(`${API_BASE_URL}/api/realtime/analyze`, { image: imageDataUrl, sessionId: currentSessionId }, { headers: { 'Content-Type': 'application/json' } } );
      if (response.data?.status) { /* 处理成功响应, 更新 analysisResult */
          const backendStatusKey = response.data.status; const frontendStatus = fatigueStatusMap[backendStatusKey];
          if (frontendStatus) { setAnalysisResult(frontendStatus); }
          else { console.warn(`sendFrameToAPI: 后端返回了未知的状态 '${backendStatusKey}'`); setAnalysisResult(fatigueStatusMap['未知'] || { text: backendStatusKey, icon: <ErrorOutlineIcon/>, color: 'default' }); }
          if (error.startsWith('API 通信错误') || error.startsWith('当前会话')) { setError(''); }
      } else if (response.data?.error?.includes("会话 ID 无效")) { /* 处理会话无效 */
          console.warn(`sendFrameToAPI: 后端返回会话无效错误 for ${currentSessionId}.`); setError(`当前会话 (${currentSessionId?.substring(0, 8)}...) 已过期或无效，监控已自动停止。`);
          if (isMonitoring) { stopMonitoringInternal(false); }
      } else { /* 处理非预期成功响应 */
           console.warn("sendFrameToAPI: API 返回了非预期的成功响应数据:", response.data); setAnalysisResult(fatigueStatusMap['分析错误']);
      }
    } catch (err) { /* 处理 API 错误, 调用 stopMonitoringInternal(false) */
      console.error(`sendFrameToAPI: 帧分析 API 调用失败 (Session: ${currentSessionId}):`, err);
      if (err.response) { /* 服务器错误 */
        const status = err.response.status; const serverError = err.response.data?.error || '未知服务器错误';
        if (status === 404 && serverError.includes("会话 ID 无效")) { /* 会话无效 404 */
          setError(`当前会话 (${currentSessionId?.substring(0, 8)}...) 已过期或无效，监控已自动停止。`); if (isMonitoring) { stopMonitoringInternal(false); }
        } else { /* 其他服务器错误 */
          setError(`API 通信错误: 服务器错误 ${status} - ${serverError}`); setAnalysisResult(fatigueStatusMap['分析错误']); if (isMonitoring) { console.warn(`sendFrameToAPI: 因服务器错误 ${status}，将停止监控循环。`); stopMonitoringInternal(false); }
        }
      } else if (err.request) { /* 网络错误 */
        setError('API 通信错误: 无法连接到分析服务器。请检查后端服务是否运行。'); setAnalysisResult(fatigueStatusMap['分析错误']); if (isMonitoring) { console.warn(`sendFrameToAPI: 因无法连接服务器，将停止监控循环。`); stopMonitoringInternal(false); }
      } else { /* 请求设置错误 */
        setError(`API 通信错误: ${err.message}`); setAnalysisResult(fatigueStatusMap['分析错误']); if (isMonitoring) { console.warn(`sendFrameToAPI: 因请求设置错误，将停止监控循环。`); stopMonitoringInternal(false); }
      }
    } finally { isProcessingRef.current = false; }
  }, [sessionId, isMonitoring, stopMonitoringInternal, error]);

  // analysisLoop 
  const analysisLoop = useCallback(async () => {
      const currentSessionId = sessionId;
      if (!isMonitoring || isProcessingRef.current || isStoppingRef.current) return;
      if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) { console.warn("Analysis loop: 视频流似乎已停止..."); setError("视频流中断..."); stopMonitoringInternal(false); return; }
      if (!currentSessionId) { console.error("Analysis loop: Session ID 丢失..."); setError("内部错误：会话丢失..."); stopMonitoringInternal(false); return; }
      const imageDataUrl = captureFrame();
      if (imageDataUrl) { await sendFrameToAPI(imageDataUrl); }
      else { console.log("Analysis loop: 帧捕获失败..."); clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = setTimeout(analysisLoop, FRAME_ANALYSIS_DELAY + 100); return; }
      if (isMonitoring && !isStoppingRef.current) { clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = setTimeout(analysisLoop, FRAME_ANALYSIS_DELAY); }
      else { clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = null; }
  }, [sessionId, isMonitoring, captureFrame, sendFrameToAPI, stopMonitoringInternal]);

  useEffect(() => {
    if (isMonitoring && sessionId && !isStoppingRef.current) {
        console.log(`Effect[isMonitoring, sessionId]: 监控已启动 (Session: ${sessionId.substring(0,8)}...), 准备启动分析循环。`);
        isProcessingRef.current = false; clearTimeout(nextFrameTimeoutRef.current);
        nextFrameTimeoutRef.current = setTimeout(analysisLoop, 100); 
    } else {
        console.log(`Effect[isMonitoring, sessionId]: 监控未激活或无 Session ID 或正在停止，确保分析循环停止。`);
        clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = null; isProcessingRef.current = false;
    }
    return () => { console.log("Effect Cleanup: 清除分析循环定时器。"); clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = null; };
  }, [isMonitoring, sessionId, analysisLoop]);


  useEffect(() => {
    console.log("Effect[]: 创建隐藏的 Canvas。"); canvasRef.current = document.createElement('canvas');
    return () => { console.log("Effect Cleanup[]: 销毁 Canvas 引用。"); canvasRef.current = null; };
  }, []);


  const startMonitoring = useCallback(async () => {
    if (isLoading || isMonitoring || isStoppingRef.current) { /* ... 防重入日志 ... */ return; }
    console.log(`startMonitoring: 请求启动监控流程...`);
    setError(''); setAnalysisResult(null); setReportUrl(null); setIsLoading(true);
    setIsMonitoring(false); setSessionId(null); isStoppingRef.current = false;

    let backendSessionId = null;
    let mediaStream = null;

    try {
      console.log("startMonitoring: 请求后端创建新会话...");
      const response = await axios.post(`${API_BASE_URL}/api/realtime/start`);
      if (response.data?.sessionId) { backendSessionId = response.data.sessionId; console.log(`startMonitoring: 后端会话已创建: ${backendSessionId}`); }
      else { throw new Error("后端未能返回有效的会话 ID。"); }
      if (!navigator.mediaDevices?.getUserMedia) { throw new Error('浏览器不支持访问摄像头 (getUserMedia)。'); }
      console.log("startMonitoring: 请求摄像头访问权限...");
      const constraints = { video: { facingMode: 'user', width: { ideal: 640 } }, audio: false };
      mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log("startMonitoring: 摄像头访问权限已获取。");

      // --- 3. 将媒体流连接到 video 元素并播放 ---
      if (videoRef.current) {
          console.log("startMonitoring: 将媒体流附加到 video 元素...");
          videoRef.current.srcObject = mediaStream;
          videoRef.current.muted = true;
          videoRef.current.playsInline = true;

          //处理 onloadedmetadata 的 Promise ---
          await new Promise((resolve, reject) => {
              const videoElement = videoRef.current; // 引用当前 video 元素
              if (!videoElement) { reject(new Error("Video 元素引用丢失")); return; }

              let timeoutId = null; // 超时定时器 ID

              // 定义核心处理函数 (播放视频)
              const playVideo = () => {
                  console.log("startMonitoring (playVideo): 尝试播放...");
                  videoElement.play()
                      .then(() => {
                          console.log("startMonitoring (playVideo): 播放成功。");
                          clearTimeout(timeoutId); // 清除超时
                          resolve(); // Promise 完成
                      })
                      .catch(playError => {
                          console.error("startMonitoring (playVideo): 播放失败:", playError);
                          clearTimeout(timeoutId); // 清除超时
                          reject(playError); // Promise 拒绝
                      });
              };

              // 定义 onloadedmetadata 事件处理器
              const handleMetadataLoaded = () => {
                  console.log("startMonitoring (handleMetadataLoaded): 元数据已加载。");
                  clearTimeout(timeoutId); // 清除超时
                  // 在元数据加载后调用核心处理函数
                  playVideo();
                  // 清理事件监听器，防止重复触发 
                  videoElement.onloadedmetadata = null;
                  videoElement.onerror = null;
              };

              // 定义错误处理器
              const handleError = (e) => {
                  console.error("startMonitoring (handleError): 加载视频流出错:", e);
                  clearTimeout(timeoutId); // 清除超时
                  reject(new Error("加载视频流出错。"));
                  // 清理事件监听器
                  videoElement.onloadedmetadata = null;
                  videoElement.onerror = null;
              };

              // 设置超时
              timeoutId = setTimeout(() => {
                  console.error("startMonitoring: 加载视频元数据超时。");
                  reject(new Error("加载视频元数据超时。"));
                  // 清理事件监听器
                  videoElement.onloadedmetadata = null;
                  videoElement.onerror = null;
              }, 10000); // 10 秒超时

              // 绑定事件监听器
              videoElement.onloadedmetadata = handleMetadataLoaded;
              videoElement.onerror = handleError;

               // --- 如果视频已经有足够的元数据，可能 onloadedmetadata 不会再次触发 ---
               // --- 添加检查，如果已准备好，直接尝试播放 ---
               if (videoElement.readyState >= videoElement.HAVE_METADATA) {
                   console.log("startMonitoring: 检测到视频元数据已存在，直接尝试播放。");
                   // 清除定时器和事件监听，防止重复执行
                   clearTimeout(timeoutId);
                   videoElement.onloadedmetadata = null;
                   videoElement.onerror = null;
                   playVideo(); // 直接调用播放
               }
          });
          // --- Promise 处理结束 ---

          // --- 4. 成功启动，更新状态 ---
          setStream(mediaStream);
          setSessionId(backendSessionId);
          setIsLoading(false);
          setIsMonitoring(true); // 最后设置
          console.log(`startMonitoring: 监控流程成功启动 (Session: ${backendSessionId.substring(0,8)}...)。`);

      } else { throw new Error("内部错误：无法访问 video 元素。"); }

    } catch (err) { 
      console.error(`startMonitoring: 启动监控失败:`, err);
      let userErrorMessage = `启动失败: ${err.message}`;
      if (err.name === 'NotAllowedError') { userErrorMessage = '启动失败：您需要允许浏览器访问摄像头权限。'; }
      else if (err.name === 'NotFoundError') { userErrorMessage = '启动失败：找不到合适的摄像头设备。'; }
      else if (err.message.includes("后端未能返回有效的会话 ID")) { userErrorMessage = '启动失败：无法从服务器获取会话，请检查后端服务。'; }
      setError(userErrorMessage);
      console.log("startMonitoring: 启动失败，开始清理资源...");
      mediaStream?.getTracks().forEach(track => track.stop()); setStream(null);
      if (videoRef.current) videoRef.current.srcObject = null;
      setIsMonitoring(false); setAnalysisResult(null); setIsLoading(false); setSessionId(null);
      clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = null; isProcessingRef.current = false; isStoppingRef.current = false;
      if (backendSessionId) { /* 尝试通知后端停止 */
          console.warn(`startMonitoring: 启动中断，尝试停止无效会话 ${backendSessionId}...`);
          axios.post(`${API_BASE_URL}/api/realtime/stop`, { sessionId: backendSessionId })
              .then(() => console.log(`startMonitoring: 无效会话 ${backendSessionId} 的停止请求已发送。`))
              .catch(stopErr => console.error(`startMonitoring: 尝试停止无效会话 ${backendSessionId} 时出错:`, stopErr));
      }
      console.log("startMonitoring: 启动失败后的资源清理完成。");
    }
  }, [isLoading, isMonitoring]); // 依赖项不变

  // useEffect for component unmount cleanup
  useEffect(() => {
    const monitoringStatusAtMount = isMonitoring; const sessionIdAtMount = sessionId;
    const streamAtMount = stream; const videoElementAtMount = videoRef.current;
    return () => {
      console.log(`组件即将卸载/清理... isMonitoring: ${monitoringStatusAtMount}, sessionId: ${sessionIdAtMount?.substring(0,8)}...`);
      if (streamAtMount) { console.log(`组件卸载: 停止本地流。`); streamAtMount.getTracks().forEach(track => track.stop()); if (videoElementAtMount && videoElementAtMount.srcObject === streamAtMount) { try { videoElementAtMount.srcObject = null; } catch (e) {} } }
      clearTimeout(nextFrameTimeoutRef.current); nextFrameTimeoutRef.current = null; console.log(`组件卸载: 定时器已清除。`);
      if (monitoringStatusAtMount && sessionIdAtMount && !isStoppingRef.current) {
        console.log(`组件卸载: 处于监控状态，异步通知后端停止 ${sessionIdAtMount.substring(0,8)}...`); isStoppingRef.current = true;
        axios.post(`${API_BASE_URL}/api/realtime/stop`, { sessionId: sessionIdAtMount })
          .then(() => console.log(`组件卸载: 后端停止请求成功 for ${sessionIdAtMount}.`))
          .catch(err => { console.error(`组件卸载: 通知后端停止会话 ${sessionIdAtMount} 时出错:`, err.response?.data || err.message); })
          // .finally(() => { isStoppingRef.current = false; }); // 在卸载时 finally 可能意义不大
      } else { console.log(`组件卸载: 未通知后端停止。原因: isMonitoring=${monitoringStatusAtMount}, sessionId=${sessionIdAtMount}, isStoppingRef=${isStoppingRef.current}`); }
    };
  }, [isMonitoring, sessionId, stream]);

  // --- Render --- 
  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      {/* ... (标题, Alerts, 按钮, Chip, Video 区域) ... */}
       <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>实时疲劳监测</Typography>
       {error && (<Alert severity={error.includes("会话已过期") || error.includes("权限") ? "warning" : "error"} sx={{ mb: 2 }} icon={error.includes("会话已过期") || error.includes("权限") ? <WarningAmberIcon/> : <ErrorOutlineIcon />} > {error} </Alert> )}
       {reportUrl && !error && ( <Alert severity="success" sx={{ mb: 2 }} icon={<InsertLinkIcon fontSize="inherit" />}> 监控已停止。查看本次会话的 <Link href={reportUrl} target="_blank" rel="noopener noreferrer">统计报告</Link>。 </Alert> )}
       {!error && !isMonitoring && !isLoading && !reportUrl && ( <Alert severity="info" sx={{ mb: 2 }}>请点击“打开摄像头”按钮开始实时监测。</Alert> )}
       {!error && isMonitoring && sessionId && ( <Alert severity="success" sx={{ mb: 2 }}>摄像头已开启 (会话ID: {sessionId.substring(0, 8)}...)，正在进行疲劳分析...</Alert> )}
       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
         {!isMonitoring ? ( <Button variant="contained" startIcon={<CameraAltIcon />} onClick={startMonitoring} disabled={isLoading || isStoppingRef.current} size="large"> {isLoading ? <CircularProgress size={24} color="inherit" /> : '打开摄像头'} </Button> ) : ( <Button variant="contained" color="secondary" startIcon={<StopCircleIcon />} onClick={() => stopMonitoringInternal(true)} disabled={isLoading || isStoppingRef.current} size="large"> {isLoading ? <CircularProgress size={24} color="inherit" /> : '停止监测 & 生成报告'} </Button> )}
         {(isMonitoring || analysisResult) && ( <Chip icon={analysisResult?.icon || defaultStatus.icon} label={`状态: ${analysisResult?.text || defaultStatus.text}`} color={analysisResult?.color || defaultStatus.color} variant={(analysisResult?.color === 'error' || analysisResult?.color === 'warning') ? "filled" : "outlined"} sx={{ minWidth: '180px', fontSize: '1rem', py: 2.5 }} /> )}
       </Box>
       <Box sx={{ position: 'relative', width: '100%', maxWidth: '720px', margin: '0 auto', backgroundColor: '#111', border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
         <video ref={videoRef} playsInline muted style={{ width: '100%', display: 'block', aspectRatio: '16/9' }} />
         {isLoading && ( <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10 }}> <CircularProgress color="primary" sx={{ mb: 1 }}/> <Typography variant="body1" color="white">正在处理...</Typography> </Box> )}
         {!isMonitoring && !isLoading && !stream && ( <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.7)', pointerEvents: 'none' }}> <CameraAltIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.6)', mb: 1 }}/> <Typography variant="h6" color="rgba(255,255,255,0.8)">摄像头未开启</Typography> </Box> )}
       </Box>
       <Alert severity="info" sx={{ mt: 3 }}> <strong>提示：</strong> <ul> <li>点击“打开摄像头”将请求权限并启动一个监控会话。</li> <li>系统会持续将画面帧发送至后端进行跟踪、特征提取（如果启用）和疲劳分析。</li> <li>点击“停止监测”将结束当前会话并尝试生成统计报告。</li> <li>请确保浏览器允许访问摄像头。如果遇到错误或状态卡住，请检查浏览器控制台和后端日志，然后尝试停止并重新开始。</li> </ul> </Alert>
    </Paper>
  );
}

export default RealtimeMonitor;